import axios from 'axios'
import get from 'lodash/get'
import { QueuePlacementApiResponse, QueuePlacementData } from './types'

export interface QueuePlacementResponse {
  data: Record<string, string>
}

export const getQueuePlacement = async (
  options: {
    productId: number
    priceInCents: number
    excludeId?: number
    sizeConversionId?: number
    sellerId: number
  },
  onError: (errorMessage: string) => void
): Promise<QueuePlacementApiResponse | undefined> => {
  try {
    return axios.get('/api/selling/queue_placement', {
      params: {
        product_id: options.productId,
        // TODO: MONEY update to be price in cents throughout React
        price_in_cents: options.priceInCents,
        exclude_id: options.excludeId,
        size_conversion_id: options.sizeConversionId,
        seller_id: options.sellerId
      }
    }) as Promise<QueuePlacementApiResponse>
  } catch (response) {
    const message = get(response, 'response.data.error') || 'Unknown error'

    console.error('Error getting queue placement:', message)

    onError(message)
  }
}

export const parseQueuePlacement = (
  response: QueuePlacementApiResponse,
  collectionPrice: number,
  minimumPrice: number,
  priceManuallyUpdated: boolean
): QueuePlacementData => {
  const {
    data: { lowest_price, place_in_queue, global }
  } = response

  let newPrice = collectionPrice

  if (!priceManuallyUpdated) {
    const minPrice = lowest_price - PRICE_STEP

    newPrice = minPrice < Number(minimumPrice) ? minimumPrice : minPrice
  }

  return {
    price: newPrice,
    lowestPrice: lowest_price,
    placeInQueue: place_in_queue
  }
}

export const roundUpToNearest = (amount: number, nearest: number) => {
  const minPrice = Number(parseFloat(amount.toString()).toFixed(2))

  return Math.ceil(minPrice / nearest) * nearest
}

export const getNewPrice = (e, minimumPrice: number, t) => {
  e.preventDefault()
  const { value } = e.target

  if (Number.isNaN(value)) {
    return
  }

  let error
  let price = 0

  if (value % 1 !== 0) {
    price = Math.ceil(value)
    error = t('react_components.selling.auto_whole_number_rounded_err')
  } else {
    price = Number.parseInt(value, 10)
  }

  if (value < minimumPrice) {
    price = minimumPrice
  }

  return { price, error }
}

export const MINIMUM_LIST_PRICE = 15
export const PRICE_STEP = 1
export const DEBOUNCE_DELAY = 750

export interface PricingSuggestion {
  price: number
  label: 'average' | 'last_sold'
}

export const getPricingSuggestions = async (options: {
  productId: number
  sizeConversionId: number
  sellerId: number
}): Promise<PricingSuggestion | null> => {
  try {
    const response = await axios.get('/api/selling/pricing_suggestions', {
      params: {
        product_id: options.productId,
        size_conversion_id: options.sizeConversionId,
        seller_id: options.sellerId
      }
    })

    return response.data.suggestion ?? null
  } catch {
    return null
  }
}

export interface BulkPricingItem {
  productId: number
  sizeConversionId: number
  priceInCents: number
  excludeId?: number
}

export interface BulkPricingResult {
  product_id: number
  size_conversion_id: number
  suggestion: PricingSuggestion | null
  place_in_queue: number
  lowest_price: number | null
}

export const getBulkPricingData = async (
  items: BulkPricingItem[]
): Promise<BulkPricingResult[]> => {
  try {
    const response = await axios.post('/api/selling/bulk_pricing_data', {
      items: items.map((item) => ({
        product_id: item.productId,
        size_conversion_id: item.sizeConversionId,
        price_in_cents: item.priceInCents,
        exclude_id: item.excludeId
      }))
    })

    return response.data.results ?? []
  } catch {
    return []
  }
}
